import { useCallback, useContext, useRef, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { AdyenComponentContainer } from '@/components/expressPayment/AdyenComponentContainer';
import { useAdyenApplePay } from '@/components/Wallets/AdyenApplePay/useAdyenApplePay';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { useIsWalletZipTaxActive } from '@/hooks/useIsWalletZipTaxActive';
import { useTranslate } from '@/hooks/useTranslate';
import { useApplePayLoadTracking } from '@/hooks/useApplePayLoadTracking';
import { getZipCodeErrorMessageKey } from '@/utils/zipCode';
import { trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';
import { useExpressWalletFallback } from '@/hooks/useExpressWalletFallback';
import { useExpressReadyTimeout } from '@/hooks/useExpressReadyTimeout';
import { useExpressReveal } from '@/context/ExpressRevealContext';
import { WalletButtonShell, type WalletReadyState } from '@/components/Wallets/WalletButton/WalletButtonShell';
import { ApplePayButtonPlaceholder } from '@/components/Wallets/WalletButton/ApplePayButtonPlaceholder';
import PaymentMethodNotSupported from '@/components/PaymentMethod/PaymentMethodNotSupported';
import { armApplePayTabFallbackIfEligible } from '@/utils/applePayTabFallback';
import { EXPRESS_WALLET_NAME, WALLET_AVAILABILITY_REASON } from '@/utils/adyenWalletAvailability';
import type { ExpressCheckoutComponentProps } from '@/types/expressCheckout';

interface ApplePayAdyenFormProps extends Pick<ExpressCheckoutComponentProps, 'isExpress'> {
  onInvalidTaxClick?: () => void;
}

export const ApplePayAdyenForm = ({ onInvalidTaxClick, isExpress }: ApplePayAdyenFormProps) => {
  const context = useContext(CheckoutContext);
  const {
    customer,
    template,
    featureFlags,
    payment: { isFormValid },
    actions: { setError },
  } = context;
  const { t } = useTranslate();
  // When the wallet-tax pipeline is on there's no on-page ZIP to validate.
  // Behind a ref so the mount-time handler closure always reads the current
  // value without re-creating the Adyen component.
  const isWalletZipTaxActive = useIsWalletZipTaxActive();
  const isWalletZipTaxActiveRef = useRef(isWalletZipTaxActive);
  isWalletZipTaxActiveRef.current = isWalletZipTaxActive;
  // PREFERRED defers the whole-screen reveal until Apple Pay is confirmed (the
  // host view shows a skeleton meanwhile). Here it just arms the readiness
  // timeout so a wallet that never resolves still falls back to DEFAULT.
  const deferReveal = template.features.deferExpressWalletReveal;
  const isFormValidRef = useRef(isFormValid);
  isFormValidRef.current = isFormValid;
  const [state, setState] = useState<WalletReadyState>('loading');
  const mountTimeRef = useRef(Date.now());
  // Bridges Adyen's `isAvailable` signal to the active template so the
  // FTD / last-method PREFERRED_PAYMENT_METHOD branches can swap to
  // DEFAULT when Apple Pay is configured but the device can't show it.
  // Without this, the template was stranding the user on an empty
  // primary CTA. Standalone AdyenApplePay already does this; the
  // `<Payment />`-mounted form variant was the missing link.
  const reportWalletAvailability = useExpressWalletFallback();
  // When a host view (PREFERRED) is gating the whole screen behind a skeleton,
  // tell it to reveal once Apple Pay is confirmed available.
  const reveal = useExpressReveal();
  // Anchored at this form's mount; closed in handleReady when Apple Pay is ready.
  const trackApplePayReady = useApplePayLoadTracking(ProviderEnum.ADYEN);

  const onClick = useCallback(
    (resolve: () => void, reject: () => void) => {
      if (isWalletZipTaxActiveRef.current || isFormValidRef.current) {
        trackAndNotifyPaymentClick(context, { paymentMethodName: PaymentMethodEnum.APPLE_PAY });
        resolve();
      } else {
        if (onInvalidTaxClick) {
          onInvalidTaxClick();
        } else {
          const errorMessageKey = getZipCodeErrorMessageKey(customer ?? {});
          setError(t(errorMessageKey));
        }
        reject();
      }
    },
    [context, customer, onInvalidTaxClick, setError, t]
  );

  const handleReady = useCallback(
    (ready?: boolean, reason?: string) => {
      const isAvailable = ready === true;
      // Domain not supported in this frame: arm the fake new-tab button.
      // Parent `ApplePayForm` remounts as ApplePayTabFallback.
      if (!isAvailable && armApplePayTabFallbackIfEligible(featureFlags)) return;
      if (isAvailable) trackApplePayReady();
      reportWalletAvailability(isAvailable, {
        wallet: EXPRESS_WALLET_NAME.APPLE_PAY,
        provider: ProviderEnum.ADYEN,
        reason: isAvailable ? undefined : (reason ?? WALLET_AVAILABILITY_REASON.UNKNOWN),
        loadTimeMs: Date.now() - mountTimeRef.current,
      });
      if (isAvailable) reveal?.onWalletReady();
      setState(isAvailable ? 'ready' : 'unavailable');
    },
    [reportWalletAvailability, reveal, trackApplePayReady, featureFlags]
  );

  // Safety net for the deferred-reveal path: if Adyen never settles
  // `isAvailable()` (hung init), don't strand the player on a permanent
  // skeleton — treat the silence as unavailable and fall back.
  useExpressReadyTimeout(deferReveal && state === 'loading', () =>
    handleReady(false, WALLET_AVAILABILITY_REASON.TIMEOUT)
  );

  const { applePayElement } = useAdyenApplePay({
    onClick,
    onReady: handleReady,
  });

  if (state === 'unavailable') {
    return <PaymentMethodNotSupported isExpress={isExpress} />;
  }

  return (
    <WalletButtonShell
      state={state}
      placeholder={<ApplePayButtonPlaceholder />}
      testId="wallet-btn-apple-tile"
      usePlaceholderAsVisual
    >
      <AdyenComponentContainer element={applePayElement} />
    </WalletButtonShell>
  );
};
